import type { SortingState } from "@tanstack/react-table";

import clsx from "clsx";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { KebabAction } from "./components/KebabMenu";
import type { KebabActionContext } from "./components/SubIBsTable";
import type { AssetGroup, Hoid, LookupResult } from "./rebate-programs/data";
import type { SubIB } from "./types";

import { Button as ButtonV2 } from "../Button";
import { useIsMobile } from "../hooks/useIsMobile";
import { PlusIcon } from "../Icons";
import { TabsComponent } from "../Tabs";
import { Typo } from "../Typo";
import { EditCommentModal } from "./components/EditCommentModal";
import { SubIBDetailDrawer } from "./components/SubIBDetailDrawer";
import { SubIBsFilterModal } from "./components/SubIBsFilterModal";
import { SubIBsTable } from "./components/SubIBsTable";
import { SubIBStatCards } from "./components/SubIBStatCards";
import { SubIBsToolbar } from "./components/SubIBsToolbar";
import { Toast } from "./components/Toast";
import { useSubIBReportContext } from "./providers/SubIBReportProvider";
import {
  SharedRebateProgramsProvider,
  type SharedRebateProgramsResources,
  useSharedRebateProgramsContext,
} from "./rebate-programs/providers/SharedRebateProgramsProvider";
import { SharedRebateProgramsView } from "./rebate-programs/SharedRebateProgramsView";
import { toDrawerSubIb } from "./rebate-programs/toDrawerSubIb";

export * from "./StatCard";
export { ALL_TIME_START } from "./providers/getDefaultDateRange";
export { buildClientsReportHref } from "./rebate-programs/buildClientsReportHref";
export type { BuildClientsReportHrefParams } from "./rebate-programs/buildClientsReportHref";
export { ReportLinkCell } from "./rebate-programs/ReportLinkCell";
export type { ReportLinkCellProps } from "./rebate-programs/ReportLinkCell";
export { StepUpCancelledError } from "./rebate-programs/StepUpCancelledError";
export { SubIBReportProvider } from "./providers/SubIBReportProvider";
export type { SubIBReportResources } from "./providers/SubIBReportProvider";
export type {
  SharedRebateProgramsResources,
  StepUpResource,
} from "./rebate-programs/providers/SharedRebateProgramsProvider";
export type { SharedRebateProgramsParams } from "./rebate-programs/providers/SharedRebateProgramsContext";
export type {
  CreateProgramApiOptions,
  CreateProgramDisplay,
  CreateProgramRestorePartner,
  CreateProgramErrorKind,
  CreateProgramInput,
  CreateProgramOutcome,
  CreateProgramOutput,
  CreateProgramStepUp,
  Hoid,
  HoidPartner,
  LookupExistingRelationship,
  LookupPartner,
  LookupResult,
  PendingCreateResume,
  SymbolOverride,
} from "./rebate-programs/data";
export type {
  AggregateStatus,
  ChildDisplayStatus,
  HoidStatus,
  KycStatus,
  SubIB,
  SubIBHoid,
  SubIBHoidPartner,
  SubIBParams,
  SubIBStats,
  SubIBTableData,
} from "./types";

type DrawerEntry =
  | { kind: "sub-ib"; id: string }
  | { kind: "program"; id: string };

/**
 * Doubles as both the `TabsComponent` tab id AND the `?tab=` query-param
 * value that selects it (see `initialTab`/`onTabChange` below). Exported so
 * the portal doesn't need to duplicate these as magic strings.
 */
export const TAB_SUB_IBS = "sub-ibs";
export const TAB_REBATE_PROGRAMS = "shared-rebate-program";

// Connected to SharedRebateProgramsProvider — must render under it. Opens the
// real CreateProgramWizard. Available regardless of which tab is active —
// creating a rebate program isn't scoped to the Rebate Programs tab alone.
// No margin of its own — callers that need spacing around it own that
// themselves, so this button's placement in the mobile header (gap-4 rhythm)
// isn't silently padded by a margin meant for a different layout.
const NewRebateProgramButton = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { requestCreateProgram, statusPending } =
    useSharedRebateProgramsContext();
  return (
    <ButtonV2
      variant="brand"
      size="md"
      onClick={() => requestCreateProgram()}
      // Held until the status read resolves — same reason as the desktop
      // toolbar's button in SharedRebateProgramsView.
      isLoading={statusPending}
      className={className}
      data-testid="sub_ib_report_header_new_program_button"
    >
      <div className="flex items-center gap-2">
        <PlusIcon className="h-4 w-4" />
        {/* Same key as the desktop toolbar's button: the design registry has
            one `cta.newProgram` entry at platform "both", so the two surfaces
            must not be able to drift apart. This label was previously
            hardcoded, which is how it came to differ from the toolbar's. */}
        {t("subIBReport_newProgram", "New rebate program")}
      </div>
    </ButtonV2>
  );
};

// Mobile page-header entry point. With no rebate programs at all the empty
// state carries the only "New rebate program" action, so the header one goes.
const MobileHeaderNewProgramButton = () => {
  const { splitEmptyState, hasNoRebatePrograms } =
    useSharedRebateProgramsContext();
  if (splitEmptyState && hasNoRebatePrograms) return null;
  return (
    <div className="self-start">
      <NewRebateProgramButton />
    </div>
  );
};

/**
 * Wraps the Sub IB drawer so its back affordance can reopen the program drawer.
 * `openProgramDetails` lives in the Shared rebate programs context, which only a
 * child of `SharedRebateProgramsProvider` can read — `SubIBReportPage` renders
 * that provider, so it cannot call the hook itself.
 */
const SubIbDrawerWithBack = ({
  backTarget,
  onPopReturn,
  onPushReturn,
  subIbId,
  ...props
}: React.ComponentProps<typeof SubIBDetailDrawer> & {
  /**
   * Where Back goes. Popping out of this drawer always lands on a program (you
   * can only reach it from a program's Rebate split), and opening one needs the
   * programs context — hence the handling lives here rather than on the page.
   */
  backTarget: { kind: "program"; id: string } | undefined;
  /** Clears this drawer and drops the top of the page's return stack. */
  onPopReturn: () => void;
  /** Records this drawer as the return target before hopping to a program. */
  onPushReturn: () => void;
  /** Selected Sub IB id, used to resolve the row from the programs roster. */
  subIbId: string | null;
}) => {
  const { openProgramDetails, roster, allPrograms } =
    useSharedRebateProgramsContext();

  // Opened from a program's Rebate split → resolve the partner from the *same*
  // dataset that program came from. `props.row` is looked up in the portal's
  // separate Sub IBs mock, where the id resolves to a different person (or to
  // nobody), which left the drawer blank. See `toDrawerSubIb`.
  const rosterRow = backTarget
    ? roster.find((s) => s.id === subIbId)
    : undefined;

  return (
    <SubIBDetailDrawer
      {...props}
      row={rosterRow ? toDrawerSubIb(rosterRow) : props.row}
      onViewProgram={(hoid) => {
        // Only navigate when the program exists in the roster the drawer feeds
        // from — a HOID from the portal's separate Sub IBs mock (`44xx`) has no
        // match there and would open an empty drawer.
        if (!allPrograms.some((p) => p.id === hoid.id)) {
          props.onViewProgram(hoid);
          return;
        }
        onPushReturn();
        openProgramDetails(hoid.id);
      }}
      isLoading={rosterRow ? false : props.isLoading}
      isError={rosterRow ? false : props.isError}
      onBack={
        backTarget
          ? () => {
              onPopReturn();
              openProgramDetails(backTarget.id);
            }
          : undefined
      }
    />
  );
};

export const SubIBReportPage = ({
  sharedRebateProgramsResources,
  lookupSubIb,
  saveComment,
  savePermissions,
  showSubIBsTab = false,
  phase2Enabled = false,
  showStepUp = false,
  resumeArchive,
  reopenArchive,
  onReopenArchiveHandled,
  getPerformanceHref,
  onNavigateToPerformance,
  getClientsHref,
  getTotalEarningsHref,
  getClientDataHref,
  onNavigateToClientsReport,
  assets,
  initialProgramSearch,
  initialTab,
  onTabChange,
}: {
  /**
   * Stats API for the "Shared rebate programs" tab, forwarded to
   * SharedRebateProgramsProvider — real in apps/portal, mocked elsewhere.
   */
  sharedRebateProgramsResources: SharedRebateProgramsResources;
  /**
   * Sub IB email lookup for the Create Program wizard; forwarded to
   * SharedRebateProgramsView. Real in apps/portal, defaults to the mock
   * inside LookupStep elsewhere.
   */
  lookupSubIb?: (email: string) => Promise<LookupResult>;
  /**
   * Persists a comment (design change #2, PART-3930). Rejecting surfaces the
   * inline save error and commits nothing. Omitted → the local-only behaviour
   * the mock/prototype relies on, which always succeeds.
   */
  saveComment?: (sub: SubIB, value: string) => Promise<void>;
  /**
   * Persists the permission toggles (design change #3, PART-3930). Rejecting
   * surfaces the inline save error and commits nothing.
   */
  savePermissions?: (sub: SubIB, seeNameAndEmail: boolean) => Promise<void>;
  showSubIBsTab?: boolean;
  /**
   * `part-4039-master-sub-ib-phase-2`. Currently drives the create wizard's
   * detailed lookup errors (PART-4481); further Phase 2 behaviour hangs off
   * this too. Separate from `showSubIBsTab` because Backoffice takes Phase 2
   * without the tab layout — it has no Sub IB roster to populate it with.
   */
  phase2Enabled?: boolean;
  showStepUp?: boolean;
  resumeArchive?: { hoid: Hoid };
  reopenArchive?: { hoid: Hoid; fromDrawer: boolean };
  onReopenArchiveHandled?: () => void;
  getPerformanceHref?: (hoid: Hoid) => string | undefined;
  onNavigateToPerformance?: (href: string) => void;
  /**
   * Shared Rebate Programs listing → Clients – Account report deep link for
   * the Clients column cell. Returning undefined (feature-flagged off,
   * Storybook, or clients === 0) renders the cell as plain text with no link
   * affordance.
   */
  getClientsHref?: (hoid: Hoid) => string | undefined;
  /** Same as above, for the Total earnings column → Trading report. */
  getTotalEarningsHref?: (hoid: Hoid) => string | undefined;
  /** Mobile-only: Trading report href for the detail sheet's "View client data" button. */
  getClientDataHref?: (
    hoid: Hoid,
    dateRange: { from: Date; to: Date },
  ) => string | undefined;
  /**
   * Called with the resolved href when the user clicks either cell. Lets the
   * portal/backoffice route through TanStack Router instead of a full-page
   * reload. Omitted → the cell falls back to the browser's own anchor
   * navigation (same tab).
   */
  onNavigateToClientsReport?: (href: string) => void;
  /** Live catalogue; null falls back to the mock in `catalogue.ts`. */
  assets?: AssetGroup[] | null;
  initialProgramSearch?: string;
  /**
   * Deep-link seed for which tab is active on first render, from the
   * portal's `?tab=` query param. Must be `TAB_REBATE_PROGRAMS` or
   * `TAB_SUB_IBS`; anything else (including undefined, or `TAB_SUB_IBS`
   * while `showSubIBsTab` is false) falls back to the default
   * (`TAB_REBATE_PROGRAMS`).
   */
  initialTab?: string;
  /**
   * Fired whenever the user switches tabs, so the portal can keep its own
   * `?tab=` query param in sync (via `navigate()` — this package stays
   * router-agnostic, so it reports the change rather than writing the URL
   * itself). Omitted → tab state is local-only, e.g. Storybook.
   */
  onTabChange?: (tab: string) => void;
}) => {
  const { t } = useTranslation();
  const { state, actions, resources, runResource } = useSubIBReportContext();
  const isMobile = useIsMobile();

  // `showSubIBsTab &&` guards against a stale `?tab=sub-ibs` (e.g. an old
  // bookmark) driving the tab strip while the (currently dead, Phase-2-only)
  // Sub IBs tab is flagged off.
  const [activeTabState, setActiveTabState] = useState<string>(() =>
    showSubIBsTab && initialTab === TAB_SUB_IBS
      ? TAB_SUB_IBS
      : TAB_REBATE_PROGRAMS,
  );
  const activeTab = showSubIBsTab ? activeTabState : TAB_REBATE_PROGRAMS;
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * Where each Back press returns to (spec §7.3 "replace + back") — a stack, not
   * a pair of "return to X" flags, because the hops chain: a Sub IB drawer
   * reached from a program can open another program, and every level needs its
   * own return target. With flags, hopping Sub IB → program cleared the Sub IB's
   * own target, so coming back left it with no Back button at all.
   *
   * Holds only the *history*; which drawer is currently open is still
   * `selectedId` (Sub IB) and the programs provider's `selectedProgramId`.
   */
  const [returnStack, setReturnStack] = useState<DrawerEntry[]>([]);
  const backTarget = returnStack[returnStack.length - 1];
  const popReturn = () => setReturnStack((prev) => prev.slice(0, -1));
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [commentOverrides, setCommentOverrides] = useState<
    Record<string, string>
  >({});
  const [editingComment, setEditingComment] = useState<SubIB | null>(null);
  const [toastMessage, setToastMessage] = useState("");

  const tableResource = resources.table;
  const statsResource = resources.stats;

  const tableData =
    tableResource.status === "success" ? tableResource.data : undefined;
  const rows = useMemo(
    () =>
      (tableData?.rows ?? []).map((r) =>
        commentOverrides[r.id] !== undefined
          ? { ...r, comment: commentOverrides[r.id] }
          : r,
      ),
    [tableData, commentOverrides],
  );
  const stats =
    statsResource.status === "success" ? statsResource.data : undefined;

  const isTableLoading =
    tableResource.status === "idle" || tableResource.status === "loading";
  const isTableError = tableResource.status === "error";
  const isStatsLoading =
    statsResource.status === "idle" || statsResource.status === "loading";
  const isStatsError = statsResource.status === "error";

  const filterCount = useMemo(() => {
    let count = 0;
    if (state.selected.statuses && state.selected.statuses.length > 0) count++;
    if (state.selected.programs && state.selected.programs.length > 0) count++;
    return count;
  }, [state.selected]);

  const sorting: SortingState = state.sortBy
    ? [{ id: state.sortBy, desc: state.sortOrder === "desc" }]
    : [];

  const handleSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const next = typeof updater === "function" ? updater(sorting) : updater;
    const first = next[0];
    actions.setSort(
      first?.id ?? null,
      first ? (first.desc ? "desc" : "asc") : null,
    );
  };

  const handleRetry = () => {
    void runResource("table");
    void runResource("stats");
  };

  const selectedRow = rows.find((r) => r.id === selectedId) ?? null;

  // Opened straight from the table — nothing to go back to, so the return stack
  // restarts empty and the drawer shows no Back affordance.
  const handleRowClick = (row: SubIB) => {
    setReturnStack([]);
    setSelectedId((prev) => (prev === row.id ? null : row.id));
  };

  const handleToggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleEditComment = (sub: SubIB) => setEditingComment(sub);

  // Design change #2 (PART-3930): the persist step runs *before* anything
  // commits, so a rejection propagates to the editor — which keeps itself open
  // with an inline error — and leaves the row, the toast and the modal alone.
  const handleSaveComment = async (value: string) => {
    if (!editingComment) return;
    await saveComment?.(editingComment, value);
    setCommentOverrides((prev) => ({ ...prev, [editingComment.id]: value }));
    setEditingComment(null);
    setToastMessage(value.trim() ? "Comment saved" : "Comment cleared");
  };

  const handleDrawerSaveComment = async (sub: SubIB, value: string) => {
    await saveComment?.(sub, value);
    setCommentOverrides((prev) => ({ ...prev, [sub.id]: value }));
    setToastMessage(value.trim() ? "Comment saved" : "Comment cleared");
  };

  const handleKebabAction = (action: KebabAction, ctx: KebabActionContext) => {
    if (action === "view" && ctx.kind === "parent") {
      handleRowClick(ctx.sub);
      return;
    }
    if (action === "copy-link") {
      const link =
        ctx.kind === "parent"
          ? ctx.sub.hoids[0]?.referralLink
          : ctx.hoid.referralLink;
      if (link && navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(link);
      }
      setToastMessage("Link copied to clipboard");
      return;
    }
    setToastMessage("Coming soon");
  };

  return (
    <SharedRebateProgramsProvider
      resources={sharedRebateProgramsResources}
      phase2Enabled={phase2Enabled}
    >
      <div className={clsx("flex flex-col", isMobile ? "gap-8" : "gap-10")}>
        {/* Page header. Mobile canvas rhythm is a flat 16px between title,
            description, and the button (`.m-head { gap: 16px }`) — desktop
            keeps its own established 24px (`gap-6`), unaffected. */}
        <div className={clsx("flex flex-col", isMobile ? "gap-4" : "gap-6")}>
          <Typo
            element="h1"
            type="display5XL"
            smType="heading2XL"
            textColor="text-text-primary"
          >
            {t("subIBReport_pageTitle", "Your Sub IBs")}
          </Typo>
          <Typo
            element="p"
            type="bodyMD"
            smType="bodySM"
            textColor="text-text-secondary"
          >
            {t(
              "subIBReport_pageSubtitle",
              "Your sub IBs, shared rebate programs, and earnings",
            )}
          </Typo>
          {isMobile && <MobileHeaderNewProgramButton />}
        </div>

        {/* Tabs + content */}
        <TabsComponent.Controller
          activeTab={activeTab}
          onChange={(id) => {
            const tab = id as string;
            setActiveTabState(tab);
            onTabChange?.(tab);
          }}
        >
          {/* PHASE 1 DESCOPE (14 Aug 2026, design canvas): the Sub IBs tab is
              cut on desktop and mobile, so the real `showSubIBsTab` default
              (false, behind the `masterSubIbPhase2` flag) is a single-view
              page — no tab bar at all, per the canvas's `page__head`/`m-head`
              (title/subtitle only, no `<ViewTabs>`). Rendering a one-item tab
              strip in that state was a leftover bug: a hairline + single tab
              with nothing to switch to. Only render this strip when the
              (currently dead, Phase-2-only) Sub IBs tab is actually enabled.
              "New program" moved out of this row: mobile keeps it in the page
              header (above), desktop now renders it in
              SharedRebateProgramsView's toolbar, next to Export CSV. */}
          {showSubIBsTab && (
            <div
              className={clsx(
                "flex items-end border-b border-surface-tonal-bold",
                !isMobile && "justify-between",
              )}
              data-testid="subib-tabstrip"
            >
              <TabsComponent.Tabs
                tabs={[
                  {
                    id: TAB_REBATE_PROGRAMS,
                    label: "Shared rebate programs",
                  },
                  { id: TAB_SUB_IBS, label: "Sub IBs" },
                ]}
                scrollable
                containerClassNames="gap-x-6 !border-b-0"
                tabClassNames="font-sans text-base px-1 !py-2.5"
                activeTabClassNames="!border-surface-brand-default text-text-primary"
                inactiveTabClassNames="border-transparent text-text-tertiary hover:text-text-primary"
              />
              {/* mb-2: this legacy (Phase-2-only, currently flag-off) row
                  needs the button nudged up off the tab strip's baseline —
                  frozen as-is, unrelated to the mobile header's own rhythm. */}
              {!isMobile && <NewRebateProgramButton className="mb-2" />}
            </div>
          )}

          {showSubIBsTab && (
            <TabsComponent.Content id={TAB_SUB_IBS}>
              <div className="mt-10 flex flex-col">
                <SubIBsToolbar
                  filterCount={filterCount}
                  onFilterOpen={() => setFilterOpen(true)}
                  onExport={() => setToastMessage("Exporting CSV…")}
                />

                <div className="mt-10 flex flex-col">
                  <SubIBStatCards
                    stats={stats}
                    isLoading={isStatsLoading}
                    isError={isStatsError}
                  />

                  {/* TableDesktop adds its own mt-6 (24px) internally — mt-4
                    (16px) here nets exactly 40px, matching the design's
                    measured stats→table gap. */}
                  <div className="mt-4">
                    <SubIBsTable
                      rows={rows}
                      isLoading={isTableLoading}
                      isError={isTableError}
                      onRetry={handleRetry}
                      sorting={sorting}
                      onSortingChange={handleSortingChange}
                      page={state.page}
                      onPageChange={actions.setPage}
                      expanded={expanded}
                      onToggleExpand={handleToggleExpand}
                      highlightedRowId={
                        selectedId ? `parent-${selectedId}` : undefined
                      }
                      onRowClick={handleRowClick}
                      onEditComment={handleEditComment}
                      onKebabAction={handleKebabAction}
                    />
                  </div>
                </div>
              </div>
            </TabsComponent.Content>
          )}

          <TabsComponent.Content id={TAB_REBATE_PROGRAMS}>
            {/* mt-10 (40px) only when the tab strip above is actually
                visible — it's the gap from that strip's hairline to this
                content (canvas `.page{gap:40px}`, both tab panels net the
                same figure). With no strip, the outer container's own gap
                (gap-8/gap-10 above) already delivers the correct
                header→content spacing on its own; adding mt-10 on top would
                double it — the exact "New program button too far from
                search" bug reported against this page. */}
            <div
              className={clsx("flex flex-col", showSubIBsTab ? "mt-10" : "")}
            >
              <SharedRebateProgramsView
                lookupSubIb={lookupSubIb}
                getPerformanceHref={getPerformanceHref}
                onNavigateToPerformance={onNavigateToPerformance}
                getClientsHref={getClientsHref}
                getTotalEarningsHref={getTotalEarningsHref}
                getClientDataHref={getClientDataHref}
                onNavigateToClientsReport={onNavigateToClientsReport}
                assets={assets}
                initialProgramSearch={initialProgramSearch}
                // Legacy 2-tab layout (Phase-2-only, currently dead behind
                // masterSubIbPhase2) still renders its own button beside the
                // tab strip above — avoid a duplicate in the toolbar.
                showNewProgramButton={!showSubIBsTab}
                // Same flag (part-4039-master-sub-ib-phase-2) gates the
                // wizard's Relationship-step Comment field.
                showRelationshipComment={showSubIBsTab}
                phase2Enabled={phase2Enabled}
                showSubIbStatus={phase2Enabled}
                showStepUp={showStepUp}
                resumeArchive={resumeArchive}
                reopenArchive={reopenArchive}
                onReopenArchiveHandled={onReopenArchiveHandled}
                // Popping out of the program drawer always lands on a Sub IB;
                // the view closes its own drawer before calling this.
                onProgramDrawerBack={
                  backTarget?.kind === "sub-ib"
                    ? () => {
                        popReturn();
                        setSelectedId(backTarget.id);
                      }
                    : undefined
                }
              />
            </div>
          </TabsComponent.Content>
        </TabsComponent.Controller>

        {/* Filter modal */}
        <SubIBsFilterModal
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
        />

        {/* Detail drawer */}
        <SubIbDrawerWithBack
          subIbId={selectedId}
          backTarget={backTarget?.kind === "program" ? backTarget : undefined}
          onPopReturn={() => {
            popReturn();
            setSelectedId(null);
          }}
          onPushReturn={() => {
            setReturnStack((prev) =>
              selectedId ? [...prev, { kind: "sub-ib", id: selectedId }] : prev,
            );
            setSelectedId(null);
          }}
          open={selectedId !== null}
          row={selectedRow}
          isLoading={isTableLoading}
          isError={isTableError}
          onRetry={handleRetry}
          onClose={() => {
            setSelectedId(null);
            setReturnStack([]);
          }}
          onSaveComment={handleDrawerSaveComment}
          onSavePermissions={savePermissions}
          onAddProgram={() => setToastMessage("Coming soon")}
          onViewProgram={() => setToastMessage("Coming soon")}
        />

        {/* Comment edit modal */}
        {editingComment && (
          <EditCommentModal
            subName={editingComment.name}
            initial={
              commentOverrides[editingComment.id] ?? editingComment.comment
            }
            onClose={() => setEditingComment(null)}
            onSave={handleSaveComment}
          />
        )}

        <Toast message={toastMessage} onClose={() => setToastMessage("")} />
      </div>
    </SharedRebateProgramsProvider>
  );
};
